/**
 * Component Name: InvoiceDetailsTable
 * Description: A table component for displaying filtered invoice details with clickable invoice numbers and breadcrumb navigation.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to display.
 *   - filterKey (string): The key for filtering invoices ('sectionId', 'poId', 'supplierId', 'monthPeriod', 'quarterPeriod').
 *   - filterValue (string): The value to filter invoices by.
 *   - handleViewDetails (function): Callback to handle invoice detail view clicks.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - breadcrumbItems (array): List of breadcrumb items for navigation.
 *   - handleBreadcrumbClick (function): Callback for handling breadcrumb clicks.
 * Feature: Displays a sortable table of invoice details filtered by specific criteria, with clickable invoice numbers to view details and breadcrumbs for navigation in the invoice management interface.
 */
import React, { useState, useEffect } from 'react';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import idx from 'idx';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getGlobalSettingReducer } from '../../actions/global-setting.action';
import { GSTORVAT } from '../../util/enums';
import { getQuarterCalculator } from '../../routes/suppliers/utils/finance-quarter';
import { getQuarterFromDate as fallbackGetQuarterFromDate } from '../../routes/suppliers/utils/finance-quarter';
import { FILTER_KEYS, INVOICE_CSS_CLASSES, INVOICE_TABLE_COLUMNS, INVOICE_ERROR_MESSAGES, INVOICE_DATE_FORMAT, INVOICE_MONTH_FORMAT } from '../../util/enums/invoice-management';

const InvoiceDetailsTable = ({ filteredInvoices, filterKey, filterValue, handleViewDetails, projects, breadcrumbItems, handleBreadcrumbClick }) => {
    const dispatch = useDispatch();
    // Get the entire globalSetting state, specifically finance_tax
    const financeSettings = useSelector(state => state.globalSetting?.finance_tax || {});
    const [quarterCalculator, setQuarterCalculator] = useState(() => fallbackGetQuarterFromDate); // Initialize with fallback

    // Load global settings on component mount if not already available
    useEffect(() => {
        // Only dispatch if finance_tax is empty or not yet loaded
        if (!financeSettings || Object.keys(financeSettings).length === 0) {
            dispatch(getGlobalSettingReducer('FINANCE_TAX'));
        }
    }, [financeSettings]);

    // Update quarterCalculator when financeSettings change
    useEffect(() => {
        if (financeSettings && Object.keys(financeSettings).length > 0) {
            const calculator = getQuarterCalculator(financeSettings);
            setQuarterCalculator(() => calculator);
        } else {
            setQuarterCalculator(() => fallbackGetQuarterFromDate);
        }
    }, [financeSettings]);

    try {
        // Filter invoices based on filterKey
        const invoices = filteredInvoices.filter((inv) => {
            try {
                if (filterKey === FILTER_KEYS.SECTION) {
                    return inv.taskId === filterValue || inv.stageId === filterValue;
                } else if (filterKey === FILTER_KEYS.PO) {
                    return idx(inv, _ => _.PurchaseOrders.id) === filterValue;
                } else if (filterKey === FILTER_KEYS.SUPPLIER) {
                    return idx(inv, _ => _.PurchaseOrders.vendorId) === filterValue;
                } else if (filterKey === FILTER_KEYS.MONTHORPERIOD) {
                    if (filterValue === 'empty-issue-date') {
                        const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT);
                        return inv.issueDate === 'N/A' || !issueDate.isValid();
                    } else {
                        const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT);
                        return issueDate.isValid() && issueDate.format(INVOICE_MONTH_FORMAT) === filterValue;
                    }
                } else if (filterKey === FILTER_KEYS.QUARTER) {
                    if (filterValue === 'empty-issue-date') {
                        const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT);
                        return inv.issueDate === 'N/A' || !issueDate.isValid();
                    } else {
                        const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT);
                        if (issueDate.isValid()) {
                            const quarterInfo = quarterCalculator(issueDate);
                            return quarterInfo.quarterKey === filterValue;
                        }
                        return false;
                    }
                }
                return true; // Fallback to show all invoices if filterKey is unrecognized
            } catch (error) {
                console.error('Error filtering invoice:', error);
                return false;
            }
        });
        // Map invoices to the required structure
        const data = invoices.map(invoice => {
            const projectName = projects.find(p => p.id === invoice.projectId)?.name || 'N/A';
            return {
                invoiceId: invoice.invoiceId,
                invoiceNumber: invoice.invoiceNumber,
                projectId: invoice.projectId,
                projectName: projectName,
                sectionHierarchy: invoice.sectionHierarchy,
                poNumber: idx(invoice, _ => _.PurchaseOrders.id),
                procurementCode: idx(invoice, _ => _.PurchaseOrders.f_id),
                supplier: invoice.supplier,
                issueDate: invoice.issueDate,
                dueDate: invoice.dueDate,
                totalAmount: invoice.totalAmount,
                subTotal: invoice.subTotal,
                userName: invoice.userName,
                status: invoice.status
            }
        });


        const columns = [
            {
                title: INVOICE_TABLE_COLUMNS.INVOICE_NUMBER,
                field: 'invoiceNumber',
                render: (row) => (
                    <span className={INVOICE_CSS_CLASSES.INVOICE_NUMBER} onClick={() => handleViewDetails(row.invoiceId)}>
                        {row.invoiceNumber}
                    </span>
                ),
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.PROJECT,
                field: 'projectName',
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.SECTION_SUBSECTION,
                field: 'sectionHierarchy',
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.PO_NUMBER,
                field: 'poNumber',
                sortable: true,
                columnSearch: true,
                /* procurementCode is the PO number with project prefix and Suffix if null then default to po Number*/
                render: (row) => <span className={INVOICE_CSS_CLASSES.PO_NUMBER}>{row.procurementCode || row.poNumber}</span>,
            },
            {
                title: INVOICE_TABLE_COLUMNS.SUPPLIER,
                field: 'supplier',
                render: (row) => <span className={INVOICE_CSS_CLASSES.SUPPLIER_NAME}>{row.supplier || 'N/A'}</span>,
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.ISSUE_DATE,
                field: 'issueDate',
                type: 'date',
                columnSearch: true,
                sortable: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.DUE_DATE,
                field: 'dueDate',
                type: 'date',
                columnSearch: true,
                sortable: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.AMOUNT,
                field: 'totalAmount',
                class: 'text-right justify-content-end',
                render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.totalAmount)}</span>,
                sortable: true,
                columnSearch: true,
            },
            {
                title: `${INVOICE_TABLE_COLUMNS.SUB_TOTAL} \n (Ex. ${GSTORVAT})`,
                field: 'subTotal',
                class: 'text-right justify-content-end',
                columnSearch: true,
                render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.subTotal)}</span>,
                sortable: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.RECEIVED_BY,
                field: 'userName',
                sortable: true,
                columnSearch: true,
            },
        ];

        const tableItemClickHandler = (row) => {
            handleViewDetails(row.invoiceId);
        };

        return (
            <div className="table-container">
                <Breadcrumb className="table-breadcrumb">
                    {breadcrumbItems.map((item, index) => (
                        <Breadcrumb.Item
                            key={index}
                            active={item.isActive}
                            onClick={item.onClick ? () => handleBreadcrumbClick(item) : undefined}
                            className={item.onClick ? 'breadcrumb-clickable' : ''}
                        >
                            {item.label}
                        </Breadcrumb.Item>
                    ))}
                </Breadcrumb>
                <CustomTable columns={columns} data={data} pagination={true} rowClickHandler={tableItemClickHandler} />
            </div>
        );
    } catch (error) {
        console.error('Error rendering InvoiceDetailsTable:', error);
        return (
            <div className="alert alert-danger">
                <strong>Error:</strong> {INVOICE_ERROR_MESSAGES.LOAD_TABLE_ERROR}
            </div>
        );
    }
};

export default InvoiceDetailsTable;